import { createContext, useContext, useState, useCallback, useEffect, useRef, type ReactNode } from 'react';
import type { ShipmentBatchDetails } from '../lib/api';
import { useAuthStore } from '../store/auth';
import { mergeBackgroundJobUpdate } from './backgroundJobState';

const PERSISTED_JOBS_KEY = 'ecomflow_persistent_background_jobs';
const LEGACY_PERSISTED_JOBS_KEY = 'ecomflow:persistent-background-jobs';

export interface BackgroundJob {
  id: string;
  type: 'bulk_shipment' | 'bulk_invoice' | 'bulk_sync' | 'bulk_labels' | 'bulk_status' | 'bulk_trash' | 'product_reviews';
  title: string;
  total: number;
  completed: number;
  failed: number;
  status: 'running' | 'completed' | 'cancelled' | 'error';
  startedAt: Date;
  completedAt?: Date;
  error?: string;
  results?: Array<{ orderId: string; success: boolean; error?: string }>;
  serverBatchId?: string;
  shipmentBatch?: ShipmentBatchDetails;
  restored?: boolean;
}

interface BackgroundJobsContextType {
  jobs: BackgroundJob[];
  activeJob: BackgroundJob | null;
  startJob: (job: Omit<BackgroundJob, 'status' | 'startedAt' | 'results'>) => string;
  updateJobProgress: (jobId: string, update: { completed?: number; failed?: number }) => void;
  updateJob: (jobId: string, update: Partial<Omit<BackgroundJob, 'id'>>) => void;
  addJobResult: (jobId: string, result: { orderId: string; success: boolean; error?: string }) => void;
  completeJob: (jobId: string) => void;
  cancelJob: (jobId: string) => void;
  failJob: (jobId: string, error: string) => void;
  clearJob: (jobId: string) => void;
  isJobCancelled: (jobId: string) => boolean;
}

const BackgroundJobsContext = createContext<BackgroundJobsContextType | null>(null);

export function BackgroundJobsProvider({ children }: { children: ReactNode }) {
  const isAuthenticated = useAuthStore(state => state.isAuthenticated);
  const [jobs, setJobs] = useState<BackgroundJob[]>(() => {
    try {
      if (typeof window === 'undefined') return [];
      const stored = localStorage.getItem(PERSISTED_JOBS_KEY)
        ?? localStorage.getItem(LEGACY_PERSISTED_JOBS_KEY);
      localStorage.removeItem(LEGACY_PERSISTED_JOBS_KEY);
      if (!stored) return [];
      const parsed = JSON.parse(stored) as Array<Omit<BackgroundJob, 'startedAt' | 'completedAt'> & {
        startedAt: string;
        completedAt?: string;
      }>;
      return parsed.map(job => mergeBackgroundJobUpdate({
        ...job,
        startedAt: new Date(job.startedAt),
        completedAt: job.completedAt ? new Date(job.completedAt) : undefined,
        restored: true,
      }, {}));
    } catch {
      return [];
    }
  });
  const cancelledJobs = useRef<Set<string>>(new Set());

  const activeJob = jobs.find(j => j.status === 'running') || null;

  useEffect(() => {
    if (!isAuthenticated) {
      localStorage.removeItem(PERSISTED_JOBS_KEY);
      localStorage.removeItem(LEGACY_PERSISTED_JOBS_KEY);
      setJobs(previous => previous.length > 0 ? [] : previous);
      return;
    }
    const persistentJobs = jobs
      .filter(job => job.serverBatchId)
      .slice(0, 1);
    localStorage.setItem(PERSISTED_JOBS_KEY, JSON.stringify(persistentJobs));
  }, [isAuthenticated, jobs]);

  const startJob = useCallback((job: Omit<BackgroundJob, 'status' | 'startedAt' | 'results'>): string => {
    const newJob: BackgroundJob = {
      ...job,
      status: 'running',
      startedAt: new Date(),
      results: [],
      restored: false,
    };
    setJobs(prev => [
      mergeBackgroundJobUpdate(newJob, {}),
      ...prev.filter(existing => existing.type !== job.type || existing.status === 'running'),
    ]);
    cancelledJobs.current.delete(job.id);
    return job.id;
  }, []);

  const updateJobProgress = useCallback((jobId: string, update: { completed?: number; failed?: number }) => {
    setJobs(prev => prev.map(job => {
      if (job.id !== jobId) return job;
      return {
        ...job,
        completed: update.completed ?? job.completed,
        failed: update.failed ?? job.failed,
      };
    }));
  }, []);

  const updateJob = useCallback((jobId: string, update: Partial<Omit<BackgroundJob, 'id'>>) => {
    setJobs(prev => prev.map(job => job.id === jobId ? mergeBackgroundJobUpdate(job, update) : job));
  }, []);

  const addJobResult = useCallback((jobId: string, result: { orderId: string; success: boolean; error?: string }) => {
    setJobs(prev => prev.map(job => {
      if (job.id !== jobId) return job;
      return {
        ...job,
        results: [...(job.results || []), result],
        completed: job.completed + (result.success ? 1 : 0),
        failed: job.failed + (result.success ? 0 : 1),
      };
    }));
  }, []);

  const completeJob = useCallback((jobId: string) => {
    setJobs(prev => prev.map(job => {
      if (job.id !== jobId) return job;
      return {
        ...job,
        status: 'completed',
        completedAt: new Date(),
      };
    }));
  }, []);

  const cancelJob = useCallback((jobId: string) => {
    cancelledJobs.current.add(jobId);
    setJobs(prev => prev.map(job => {
      if (job.id !== jobId) return job;
      return {
        ...job,
        status: 'cancelled',
        completedAt: new Date(),
      };
    }));
  }, []);

  const failJob = useCallback((jobId: string, error: string) => {
    setJobs(prev => prev.map(job => {
      if (job.id !== jobId) return job;
      return {
        ...job,
        status: 'error',
        error,
        completedAt: new Date(),
      };
    }));
  }, []);

  const clearJob = useCallback((jobId: string) => {
    setJobs(prev => prev.filter(job => job.id !== jobId));
    cancelledJobs.current.delete(jobId);
  }, []);

  const isJobCancelled = useCallback((jobId: string) => {
    return cancelledJobs.current.has(jobId);
  }, []);

  return (
    <BackgroundJobsContext.Provider value={{
      jobs,
      activeJob,
      startJob,
      updateJobProgress,
      updateJob,
      addJobResult,
      completeJob,
      cancelJob,
      failJob,
      clearJob,
      isJobCancelled,
    }}>
      {children}
    </BackgroundJobsContext.Provider>
  );
}

export function useBackgroundJobs() {
  const context = useContext(BackgroundJobsContext);
  if (!context) {
    throw new Error('useBackgroundJobs must be used within BackgroundJobsProvider');
  }
  return context;
}
